import React, { useState, useEffect } from "react";
import DashNavbar from "./DashNavbar";
import Sidebar from "./Sidebar";
import Table from "react-bootstrap/Table";
import Pagination from "react-bootstrap/Pagination";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Spinner from "react-bootstrap/Spinner";

const CustomStyles = () => (
  <style>{`
    .interviewer-list-page.wrapper { display: flex; height: calc(100vh - 60px); overflow: hidden; }
    .interviewer-list-page .main { flex: 1; display: flex; flex-direction: column; overflow: hidden; background-color: #f8f9fa; }
    .interviewer-list-page .card { flex: 1; display: flex; flex-direction: column; overflow: hidden; border: none; border-radius: 12px; box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075); margin-bottom: 0 !important; }
    .interviewer-list-page .card-body { flex: 1; overflow-y: auto; display: flex; flex-direction: column; padding: 0 !important; }
    .interviewer-list-page .table-container { flex: 1; overflow-y: auto; padding: 0 1.5rem; }
    .interviewer-list-page .card-header { background: white; border-bottom: 1px solid #edf2f7; padding: 0.75rem 1.5rem; }
    .interviewer-list-page .card-footer { background: white; border-top: 1px solid #edf2f7; padding: 0.75rem 1.5rem; }
    .interviewer-list-page .search-container { padding: 1rem 1.5rem; flex-shrink: 0; }
    .gradient-text { background: linear-gradient(135deg, #0d6efd 0%, #0043a8 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 700; }
    
    /* Compact Table Styling */
    .interviewer-list-page .table td { padding: 8px 12px !important; font-size: 14px; }
    .interviewer-list-page .table th { padding: 12px !important; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; }
  `}</style>
);

function InterviewerDetailList() {
  const [interviewers, setInterviewers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredInterviewers, setFilteredInterviewers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchInterviewerDetails();
  }, [currentPage, searchTerm]);

  const fetchInterviewerDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://raasbackend.vetrinahealthcare.com/interviewer-details/");
      const data = await response.json();
      setInterviewers(data);
      filterInterviewers(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterInterviewers = (data) => {
    const filtered = data.filter(
      (interviewer) =>
        interviewer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interviewer.skills.toLowerCase().includes(searchTerm.toLowerCase()) ||
        interviewer.experience.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredInterviewers(filtered);
  };

  const handlePageChange = (page) => {
    const totalPages = Math.ceil(filteredInterviewers.length / itemsPerPage);
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageOptions = () => [5, 10, 25, 50, 100];

  const renderPaginationFooter = () => {
    if (loading || interviewers.length === 0) return null;
    const totalItems = filteredInterviewers.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startRecord = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endRecord = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className="card-footer d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="text-muted small">
          Showing <b>{startRecord}</b> to <b>{endRecord}</b> of <b>{totalItems}</b> records
        </div>
        
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Rows per page:</span>
            <Form.Select 
              size="sm" 
              value={itemsPerPage} 
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ width: "70px" }}
            >
              {getPageOptions().map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </Form.Select>
          </div>

          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
            <Pagination.Item active>{currentPage}</Pagination.Item>
            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} />
          </Pagination>
        </div>
      </div>
    );
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const validatePhoneNumber = (phoneNumber) => {
    // Regular expression to check if the phone number has exactly 10 digits
    const phoneRegex = /^[0-9]{10}$/;
    // Ensure the phone number is not all zeros
    if (phoneRegex.test(phoneNumber) && phoneNumber !== '0000000000') {
      return true;
    }
    return false;
  };

  return (
    <>
      <DashNavbar />
      <div className="interviewer-list-page wrapper">
        <Sidebar />
        <div className="main">
          <CustomStyles />
          <div className="container-fluid flex-grow-1 d-flex flex-column p-3 p-md-4">
            <div className="card shadow-sm">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="mb-0 gradient-text">Interviewer Details</h3>
                <InputGroup style={{ maxWidth: "400px" }} size="sm">
                  <Form.Control
                    type="search"
                    placeholder="Search by name, skill, or experience..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <InputGroup.Text className="bg-primary text-white border-primary">
                    <i className="fas fa-search"></i>
                  </InputGroup.Text>
                </InputGroup>
              </div>

              <div className="card-body">
                <div className="table-container">
                  <Table className="table table-hover align-middle mb-0">
                    <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                      <tr>
                        <th style={{ width: "120px" }}>Employee ID</th>
                        <th>Interviewer</th>
                        <th>Skills</th>
                        <th style={{ width: "150px" }}>Experience</th>
                        <th>Email</th>
                        <th style={{ width: "140px" }}>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted">Loading interviewer details...</p>
                          </td>
                        </tr>
                      ) : filteredInterviewers.length > 0 ? (
                        filteredInterviewers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((interviewer, index) => (
                          <tr key={index}>
                            <td className="text-muted">{interviewer.employee_id}</td>
                            <td className="fw-bold">{interviewer.name}</td>
                            <td className="text-muted small">{interviewer.skills}</td>
                            <td>{interviewer.experience}</td>
                            <td>{interviewer.email}</td>
                            <td>
                              {validatePhoneNumber(interviewer.phone) ? (
                                interviewer.phone
                              ) : (
                                <span className="text-danger small">Invalid Phone</span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-5 text-muted fst-italic">
                            No interviewers found matching your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
              {renderPaginationFooter()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default InterviewerDetailList;


// import React, { useState, useEffect } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import Pagination from "react-bootstrap/Pagination";

// function InterviewerDetailList() {
//   const [interviewers, setInterviewers] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [filteredInterviewers, setFilteredInterviewers] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5;

//   useEffect(() => {
//     fetchInterviewerDetails();
//   }, [currentPage, searchTerm]);

//   const fetchInterviewerDetails = async () => {
//     try {
//       const response = await fetch(`https://raasbackend.vetrinahealthcare.com/interviewer-details/?page=${currentPage}&limit=${itemsPerPage}`
//       );
//       const data = await response.json();
//       setInterviewers(data);
//       filterInterviewers(data);
//     } catch (error) {
//       console.error("Error fetching data:", error);
//     }
//   };

//   const filterInterviewers = (data) => {
//     const filtered = data.filter(
//       (interviewer) =>
//         interviewer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         interviewer.skills.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         interviewer.experience.toLowerCase().includes(searchTerm.toLowerCase())
//     );
//     setFilteredInterviewers(filtered);
//   };

//   const handlePageChange = (page) => {
//     if (page < 1 || page > totalPages) {
//       return;
//     }
//     setCurrentPage(page);
//   };

//   const totalPages = Math.ceil(filteredInterviewers.length / itemsPerPage);
//   const paginatedInterviewers = filteredInterviewers.slice(
//     (currentPage - 1) * itemsPerPage,
//     currentPage * itemsPerPage
//   );

//   const handleSearch = (e) => {
//     setSearchTerm(e.target.value);
//     setCurrentPage(1);
//   };

//   return (
//     <>
//       {/* <div
//         style={{ display: "flex", flexDirection: "column", height: "100vh" }}
//       > */}
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className=" main  dash4">
//           <div className="container mt-4">
//             <div className="auth-inner mt-5 w-100">
//               <h2 className="mb-4 gradient-text">Interviewer Details</h2>
//               <div className="input-group justify-content-center align-items-center">
//                 <div className="form-outline w-50">
//                   <input
//                     type="search"
//                     className="form-control"
//                     placeholder="Search by Name, Skill, or Experience"
//                     value={searchTerm}
//                     onChange={handleSearch}
//                   />
//                   <button className="btn btn-primary searchicon text-xs">
//                     <i className="fas fa-search"></i>
//                   </button>
//                 </div>
//               </div>
//               <div className="table-responsive">
//                 <table className="table">
//                   <thead className="thead-dark">
//                     <tr>
//                       <th scope="col">Employee ID</th>
//                       <th scope="col">Interviewer</th>
//                       <th scope="col">Skills</th>
//                       <th scope="col">Experience</th>
//                       <th scope="col">Email</th>
//                       <th scope="col">Phone</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {paginatedInterviewers.map((interviewer, index) => (
//                       <tr key={index}>
//                         <td>{interviewer.employee_id}</td>
//                         <td>{interviewer.name}</td>
//                         <td>{interviewer.skills}</td>
//                         <td>{interviewer.experience}</td>
//                         <td>{interviewer.email}</td>
//                         <td>{interviewer.phone}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//               <div className="d-flex justify-content-start mt-4">
//                 <Pagination>
//                   <Pagination.Prev
//                     onClick={() => handlePageChange(currentPage - 1)}
//                     disabled={currentPage === 1}
//                   />
//                   {Array.from({ length: totalPages }, (_, index) => (
//                     <Pagination.Item
//                       key={index + 1}
//                       active={index + 1 === currentPage}
//                       onClick={() => handlePageChange(index + 1)}
//                     >
//                       {index + 1}
//                     </Pagination.Item>
//                   ))}
//                   <Pagination.Next
//                     onClick={() => handlePageChange(currentPage + 1)}
//                     disabled={currentPage === totalPages}
//                   />
//                 </Pagination>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       {/* </div> */}
//     </>
//   );
// }

// export default InterviewerDetailList;
